/**
 * ui/layout.tsx — ส่วนประกอบจัดวางหน้า
 */

import type { ReactNode } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { cn } from '~/lib/cn';
import { Button } from './primitives';

export function PageHeader({
  title,
  description,
  actions,
  breadcrumb,
}: {
  title: string;
  description?: string;
  actions?: ReactNode;
  breadcrumb?: ReactNode;
}) {
  return (
    <div className="mb-5 flex flex-wrap items-end justify-between gap-3">
      <div className="min-w-0">
        {breadcrumb ? <div className="mb-1 text-xs text-fg-muted">{breadcrumb}</div> : null}
        <h1 className="text-xl font-semibold tracking-tight text-fg">{title}</h1>
        {description ? <p className="mt-1 text-sm text-fg-muted">{description}</p> : null}
      </div>
      {actions ? <div className="flex shrink-0 items-center gap-2">{actions}</div> : null}
    </div>
  );
}

/** แถบเครื่องมือเหนือตาราง สำหรับช่องค้นหาและตัวกรอง */
export function Toolbar({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <div
      className={cn(
        'flex flex-wrap items-end gap-2 border-b border-border-base px-3 py-3',
        className,
      )}
    >
      {children}
    </div>
  );
}

interface PaginationProps {
  page: number;
  totalPages: number;
  total: number;
  pageSize: number;
  onPageChange: (page: number) => void;
}

export function Pagination({ page, totalPages, total, pageSize, onPageChange }: PaginationProps) {
  if (total === 0) return null;

  const from = (page - 1) * pageSize + 1;
  const to = Math.min(page * pageSize, total);

  return (
    <div className="flex flex-wrap items-center justify-between gap-3 border-t border-border-base px-3 py-2.5">
      <p className="text-xs text-fg-muted">
        แสดง <span className="tabular">{from.toLocaleString('th-TH')}</span>–
        <span className="tabular">{to.toLocaleString('th-TH')}</span> จาก{' '}
        <span className="tabular">{total.toLocaleString('th-TH')}</span> รายการ
      </p>

      <div className="flex items-center gap-1">
        <Button
          size="sm"
          iconOnly
          variant="ghost"
          onClick={() => onPageChange(page - 1)}
          disabled={page <= 1}
          aria-label="หน้าก่อนหน้า"
        >
          <ChevronLeft size={16} />
        </Button>
        <span className="tabular px-2 text-xs text-fg-muted">
          {page} / {totalPages}
        </span>
        <Button
          size="sm"
          iconOnly
          variant="ghost"
          onClick={() => onPageChange(page + 1)}
          disabled={page >= totalPages}
          aria-label="หน้าถัดไป"
        >
          <ChevronRight size={16} />
        </Button>
      </div>
    </div>
  );
}

/** การ์ดตัวเลขสรุปบน dashboard */
export function StatCard({
  label,
  value,
  sublabel,
  tone = 'neutral',
  icon,
}: {
  label: string;
  value: string;
  sublabel?: string;
  tone?: 'neutral' | 'success' | 'danger';
  icon?: ReactNode;
}) {
  return (
    <div className="card p-4">
      <div className="flex items-start justify-between gap-2">
        <p className="text-xs font-medium text-fg-muted">{label}</p>
        {icon ? <span className="text-fg-subtle">{icon}</span> : null}
      </div>
      <p
        className={cn(
          'tabular mt-2 text-2xl font-semibold tracking-tight',
          tone === 'success' && 'text-success',
          tone === 'danger' && 'text-danger',
        )}
      >
        {value}
      </p>
      {sublabel ? <p className="mt-1 text-xs text-fg-muted">{sublabel}</p> : null}
    </div>
  );
}
